import React, { useState, useEffect } from 'react';
import config from '../config';
//import { getTrainingScores } from '../services/notion';
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  Tooltip
} from 'recharts';
import './TrainingScores.css';

const ScoreDisplay = ({ score, total }) => {
  const percentage = (score / total) * 100;
  let color;
  if (percentage >= 80) color = 'var(--success-color)';
  else if (percentage >= 60) color = 'var(--warning-color)';
  else color = 'var(--danger-color)';

  return (
    <div className="score-item" style={{ color }}>
      {score.toFixed(1)}/{total}
    </div>
  );
};

const ScoreChart = ({ scores }) => {
  const data = {
    labels: ['专业度', '沟通能力', '服务态度', '需求挖掘', '应变能力', '客户体验'],
    datasets: [
      {
        label: '评分',
        data: [
          scores.professionalScore.score,
          scores.communicationScore.score,
          scores.serviceScore.score,
          scores.demandScore.score,
          scores.responseScore.score,
          scores.experienceScore.score
        ],
        backgroundColor: 'rgba(0, 168, 255, 0.2)',
        borderColor: 'rgba(0, 168, 255, 0.8)',
        borderWidth: 2,
        pointBackgroundColor: '#00a8ff',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: '#00a8ff'
      }
    ]
  };

  const options = {
    scales: {
      r: {
        angleLines: {
          color: 'rgba(255, 255, 255, 0.1)'
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)'
        },
        pointLabels: {
          color: '#ffffff',
          font: {
            size: 12
          }
        },
        suggestedMin: 0,
        suggestedMax: 10
      }
    },
    plugins: {
      legend: {
        display: false
      }
    },
    maintainAspectRatio: false
  };

  return (
    <div className="radar-chart">
      <Radar data={data} options={options} />
    </div>
  );
};

// 修改时间格式化函数
const formatTime = (dateString) => {
  if (!dateString) return '';
  try {
    const [datePart, timePart] = dateString.split(' ');
    const [hours, minutes] = timePart.split(':');
    
    // 增加8小时（而不是12小时）
    let newHours = parseInt(hours) + 8;
    
    // 处理超过24小时的情况
    if (newHours >= 24) {
      newHours -= 24;
    }
    
    // 返回格式化的时间
    return `${datePart} ${String(newHours).padStart(2, '0')}:${minutes}`;
  } catch (error) {
    console.error('Date parsing error:', error);
    return dateString;
  }
};

const DetailModal = ({ score, onClose }) => {
  // 准备表情占比数据
  const expressionData = [
    { name: '微笑', value: score.expressionData?.smile || 0, color: '#4CAF50' },
    { name: '无表情', value: score.expressionData?.neutral || 0, color: '#2196F3' },
    { name: '思考', value: score.expressionData?.thinking || 0, color: '#FF9800' },
    { name: '皱眉', value: score.expressionData?.frown || 0, color: '#F44336' },
    { name: '其他', value: score.expressionData?.other || 0, color: '#9E9E9E' }
  ].filter(item => item.value > 0); // 只显示有值的项目

  const COLORS = expressionData.map(item => item.color);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div 
        className="modal-content" 
        onClick={e => e.stopPropagation()}
        style={{
          backgroundColor: 'var(--card-bg)',
          color: 'var(--text-primary)'
        }}
      >
        <button 
          className="close-button" 
          onClick={onClose}
          style={{ color: 'var(--text-primary)' }}
        >
          ×
        </button>
        <h2 style={{ color: 'var(--text-primary)' }}>详细评价 - {formatTime(score.date)}</h2>
        
        {/* 评分详情 */}
        <div className="modal-scores">
          <div className="score-detail">
            <h4>共情能力 <ScoreDisplay score={score.empathyScore.score} total={score.empathyScore.total} /></h4>
            <p>{score.empathyScore.comment || '暂无评语'}</p>
          </div>
          <div className="score-detail">
            <h4>引导能力 <ScoreDisplay score={score.guidanceScore.score} total={score.guidanceScore.total} /></h4>
            <p>{score.guidanceScore.comment || '暂无评语'}</p>
          </div>
          <div className="score-detail">
            <h4>安全感建立 <ScoreDisplay score={score.securityScore.score} total={score.securityScore.total} /></h4>
            <p>{score.securityScore.comment || '暂无评语'}</p>
          </div>
          <div className="score-detail">
            <h4>语言适应性 <ScoreDisplay score={score.languageScore.score} total={score.languageScore.total} /></h4>
            <p>{score.languageScore.comment || '暂无评语'}</p>
          </div>
          <div className="score-detail">
            <h4>情绪管理 <ScoreDisplay score={score.emotionScore.score} total={score.emotionScore.total} /></h4>
            <p>{score.emotionScore.comment || '暂无评语'}</p>
          </div>
        </div>

        {/* 表情占比图表 */}
        {expressionData.length > 0 && (
          <div className="expression-chart-section">
            <h4>表情占比分析</h4>
            <div className="expression-chart">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={expressionData}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {expressionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `${value}%`} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* 聊天记录部分 */}
        {score.chatHistory && (
          <div className="chat-history-section">
            <h4>训练对话记录</h4>
            <div className="chat-history">
              {score.chatHistory.split('\n').map((line, index) => {
                // 判断说话者角色
                const isTeacher = line.includes('老师:') || line.startsWith('老师');
                const roleClass = isTeacher ? 'teacher' : 'student';
                
                return (
                  <div key={index} className={`chat-message ${roleClass}`}>
                    {line}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* 总体评价 */}
        <div className="modal-summary">
          <h4>总体评价</h4>
          <p>{score.summary}</p>
        </div>
      </div>
    </div>
  );
};

const ScoreCard = ({ score, onClick }) => {
  const [showDetail, setShowDetail] = useState(false);
  const [activeChart, setActiveChart] = useState('radar');
  const [prevChart, setPrevChart] = useState(null);

  // 处理图表切换
  const handleChartSwitch = (chartType) => {
    if (chartType !== activeChart) {
      setPrevChart(activeChart);
      setActiveChart(chartType);
    }
  };

  // 准备表情占比数据
  const expressionData = [
    { name: '微笑', value: score.expressionData?.smile || 0, color: '#4CAF50' },
    { name: '无表情', value: score.expressionData?.neutral || 0, color: '#2196F3' },
    { name: '思考', value: score.expressionData?.thinking || 0, color: '#FF9800' },
    { name: '皱眉', value: score.expressionData?.frown || 0, color: '#F44336' },
    { name: '其他', value: score.expressionData?.other || 0, color: '#9E9E9E' }
  ].filter(item => item.value > 0);

  const COLORS = expressionData.map(item => item.color);

  // 雷达图数据
  const radarData = [
    { subject: '共情', A: score.empathyScore?.score || 0, fullMark: 10 },
    { subject: '引导', A: score.guidanceScore?.score || 0, fullMark: 10 },
    { subject: '安全感', A: score.securityScore?.score || 0, fullMark: 10 },
    { subject: '语言', A: score.languageScore?.score || 0, fullMark: 10 },
    { subject: '情绪', A: score.emotionScore?.score || 0, fullMark: 10 }
  ];

  return (
    <div 
      className="score-card"
      onClick={onClick}
      style={{ 
        cursor: 'pointer',
        backgroundColor: 'var(--card-bg)',
        border: '1px solid var(--border-color)'
      }}
    >
      <div className="card-header">
        <div className="title-section">
          <h3 style={{ color: 'var(--text-primary)' }}>{score.name}</h3>
          <p className="date" style={{ color: 'var(--text-secondary)' }}>{formatTime(score.date)}</p>
        </div>
        <div className="total-score" style={{ color: 'var(--success-color)' }}>
          {Number(score.totalScore?.score || 0).toFixed(1)}/10
        </div>
      </div>
      
      <div className="chart-toggle-buttons">
        <button 
          className={`chart-toggle-button ${activeChart === 'radar' ? 'active' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            handleChartSwitch('radar');
          }}
        >
          能力分析
        </button>
        <button 
          className={`chart-toggle-button ${activeChart === 'pie' ? 'active' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            handleChartSwitch('pie');
          }}
        >
          表情分析
        </button>
      </div>
      
      <div className="chart-container">
        <div className={`chart-wrapper ${activeChart === 'radar' ? 'active' : ''} ${prevChart === 'radar' ? 'prev' : ''}`}>
          <ResponsiveContainer width="100%" height={300}>
            <RadarChart data={radarData} margin={{ top: 20, right: 30, bottom: 20, left: 30 }}>
              <PolarGrid stroke="rgba(255, 255, 255, 0.2)" />
              <PolarAngleAxis 
                dataKey="subject" 
                tick={{ fill: "var(--text-primary)", fontSize: 14, fontWeight: 500 }}
                stroke="rgba(255, 255, 255, 0.2)"
              />
              <PolarRadiusAxis 
                angle={30} 
                domain={[0, 10]} 
                tick={{ fill: "var(--text-primary)", fontSize: 12 }}
                stroke="rgba(255, 255, 255, 0.2)"
              />
              <Radar
                name="得分"
                dataKey="A"
                stroke="#82ca9d"
                fill="#82ca9d"
                fillOpacity={0.3}
                animationBegin={0}
                animationDuration={600}
                animationEasing="ease-out"
              />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        <div className={`chart-wrapper ${activeChart === 'pie' ? 'active' : ''} ${prevChart === 'pie' ? 'prev' : ''}`}>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={expressionData}
                cx="50%"
                cy="40%"
                labelLine={true}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={90}
                fill="#8884d8"
                dataKey="value"
                animationBegin={0}
                animationDuration={600}
                animationEasing="ease-out"
              >
                {expressionData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => `${value}%`} />
              <Legend 
                verticalAlign="bottom"
                align="center"
                height={36}
                wrapperStyle={{
                  bottom: 10,
                  fontSize: '12px'
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="summary">
        <p>
          <strong>总体评语：</strong>{score.summary}
        </p>
      </div>

      {showDetail && (
        <DetailModal 
          score={score} 
          onClose={() => setShowDetail(false)} 
        />
      )}
    </div>
  );
};

const TrainingScores = () => {
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortField, setSortField] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedScore, setSelectedScore] = useState(null);

  useEffect(() => {
    const fetchScores = async () => {
      try {
        console.log('Fetching from:', `${config.API_BASE_URL}/api/training-scores`); // 添加调试日志
        const response = await fetch(`${config.API_BASE_URL}/api/training-scores`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setScores(data);
        setLoading(false);
      } catch (error) {
        console.error('Fetching scores failed:', error);
        setError(error.message);
        setLoading(false);
      }
    };

    fetchScores();
  }, []);

  if (loading) return (
    <div style={{ 
      backgroundColor: 'var(--background-color)', 
      minHeight: '100vh',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      color: 'var(--text-primary)'
    }}>
      加载中...
    </div>
  );

  if (error) return (
    <div style={{ 
      backgroundColor: 'var(--background-color)', 
      minHeight: '100vh',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      color: 'var(--danger-color)'
    }}>
      错误: {error}
    </div>
  );

  // 过滤和排序逻辑
  const filteredScores = scores
    .filter(score => {
      const matchesSearch = score.summary.toLowerCase().includes(searchTerm.toLowerCase());
      const scoreDate = new Date(score.date);
      const afterStartDate = !startDate || scoreDate >= new Date(startDate);
      const beforeEndDate = !endDate || scoreDate <= new Date(endDate);
      return matchesSearch && afterStartDate && beforeEndDate;
    })
    .sort((a, b) => {
      const order = sortOrder === 'asc' ? 1 : -1;
      if (sortField === 'date') {
        return order * (new Date(a.date) - new Date(b.date));
      }
      return order * (a[sortField]?.score - b[sortField]?.score);
    });

  return (
    <div className="training-scores" style={{ 
      backgroundColor: 'var(--background-color)',
      minHeight: '100vh',
      padding: '20px'
    }}>
      <h1 style={{ color: 'var(--text-primary)' }}>培训评分记录</h1>
      
      <div className="controls" style={{
        backgroundColor: 'var(--card-bg)',
        padding: '20px',
        borderRadius: '8px',
        marginBottom: '20px'
      }}>
        <div className="search-box">
          <input
            type="text"
            placeholder="搜索评语..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              backgroundColor: 'var(--card-bg)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-color)'
            }}
          />
        </div>
        
        <div className="date-filter">
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            style={{
              backgroundColor: 'var(--card-bg)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-color)'
            }}
          />
          <span style={{ color: 'var(--text-primary)' }}>至</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            style={{
              backgroundColor: 'var(--card-bg)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-color)'
            }}
          />
        </div>
        
        <div className="sort-controls">
          <select 
            value={sortField} 
            onChange={(e) => setSortField(e.target.value)}
            style={{
              backgroundColor: 'var(--card-bg)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-color)'
            }}
          >
            <option value="date">日期</option>
            <option value="empathyScore">共情能力</option>
            <option value="guidanceScore">引导能力</option>
            <option value="securityScore">安全感建立</option>
            <option value="languageScore">语言适应性</option>
            <option value="emotionScore">情绪管理</option>
            <option value="totalScore">总分</option>
          </select>
          <button 
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            style={{
              backgroundColor: 'var(--primary-color)',
              color: 'var(--text-primary)',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {sortOrder === 'asc' ? '升序' : '降序'}
          </button>
        </div>
      </div>

      <div className="scores-list">
        {filteredScores.map((score) => (
          <ScoreCard 
            key={score.id}
            score={score}
            onClick={() => setSelectedScore(score)}
          />
        ))}
      </div>

      {selectedScore && (
        <DetailModal 
          score={selectedScore} 
          onClose={() => setSelectedScore(null)} 
        />
      )}
    </div>
  );
};

export default TrainingScores;